import { useEffect, useState } from 'react';

export type HighlightRichTextResult = {
  html: string;
  numberOfMatches: number;
};

export function highlightText(
  html: string,
  searchTerm: string,
): HighlightRichTextResult {
  if (!searchTerm?.trim()) return { html, numberOfMatches: 0 };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const textNodes: { node: Text; start: number; end: number }[] = [];
  let fullText = '';
  let offset = 0;

  // Step 1: Collect all visible text nodes and their positions
  function collectTextNodes(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      textNodes.push({
        node: node as Text,
        start: offset,
        end: offset + text.length,
      });

      fullText += text;
      offset += text.length;
    } else {
      node.childNodes.forEach(collectTextNodes);
    }
  }

  collectTextNodes(body);

  // Step 2: Find all match ranges in the full visible text
  const lowerFullText = fullText.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const matchRanges: { start: number; end: number }[] = [];

  let index = 0;
  // biome-ignore lint/suspicious/noAssignInExpressions: <needed>
  while ((index = lowerFullText.indexOf(lowerSearch, index)) !== -1) {
    matchRanges.push({ start: index, end: index + lowerSearch.length });
    index += lowerSearch.length;
  }

  if (matchRanges.length === 0) return { html, numberOfMatches: 0 };

  // Step 3: Highlight each match range
  for (const { start: matchStart, end: matchEnd } of matchRanges.reverse()) {
    for (const { node, start, end } of textNodes) {
      if (end <= matchStart || start >= matchEnd) continue; // no overlap

      const nodeText = node.textContent || '';
      const relativeStart = Math.max(0, matchStart - start);
      const relativeEnd = Math.min(nodeText.length, matchEnd - start);

      const before = nodeText.slice(0, relativeStart);
      const match = nodeText.slice(relativeStart, relativeEnd);
      const after = nodeText.slice(relativeEnd);

      const fragment = document.createDocumentFragment();

      if (before) {
        fragment.appendChild(document.createTextNode(before));
      }

      const strong = document.createElement('strong');
      strong.textContent = match;
      fragment.appendChild(strong);

      if (after) {
        fragment.appendChild(document.createTextNode(after));
      }

      node.parentNode?.replaceChild(fragment, node);
    }
  }
  return { html: body.innerHTML, numberOfMatches: matchRanges.length };
}

export type UseHighlightedTextProps = {
  html: string;
  searchTerm: string;
};

export const useHighlightedText = ({
  html,
  searchTerm,
}: UseHighlightedTextProps): HighlightRichTextResult => {
  const [highlightedText, setHighlightedText] =
    useState<HighlightRichTextResult>({ html, numberOfMatches: 0 });

  useEffect(() => {
    const result = highlightText(html, searchTerm);
    setHighlightedText(result);
  }, [html, searchTerm]);

  return highlightedText;
};
